import React, { useRef } from "react";

import { faChartColumn } from "@fortawesome/pro-regular-svg-icons";

import { OutputCard } from "./OutputCard";
import { PieChart } from "./PieChart";
import { SaveOutputControl } from "./SaveOutputControl";

import type { ChartOutput } from "../types";

import BarChart from "../../Report/Components/VisualizePanel/ReportingCharts/ChartHooks/BarChart/BarChart";
import LineChart from "../../Report/Components/VisualizePanel/ReportingCharts/ChartHooks/LineChart";

// Chart.js 4.5.1 sizes pie arcs by Math.abs(value) while tooltips show the signed value — a
// negative group would render as a lying, positive-looking slice. emitChart's backend guard
// steers new generations away from pie for negative data, but it doesn't run on artifact
// reopen, so reopened/pre-existing pies need this fallback too (round-24).
const hasNegativeValue = (values: (number | null)[]): boolean => values.some((value) => value !== null && value < 0);

// Renders an agent-computed chart. The standalone BarChart/LineChart hooks take a label-object
// array + a flat number[]; the SSE payload carries string labels + a single dataset, so we
// adapt here. Single-series only — matches what the agent emits.
export const InlineChartBubble: React.FC<{ output: ChartOutput; defaultName: string }> = ({ output, defaultName }) => {
	const labelObjects = output.labels.map((name) => ({ name }));
	const values = output.datasets[0]?.data ?? [];
	const valueLabel = output.datasets[0]?.label ?? output.valueLabel;
	// BarChart is shared with the v1 Visualize Panel and still types datasets as number[], though
	// shapeChartOutput can hand back null for any chart type — cast at this one boundary rather than
	// widening a production component's contract. Runtime behavior is unchanged (nulls already flowed).
	const barValues = values as number[];
	// BarChart/LineChart are forwardRef components whose ResizeObserver effect dereferences
	// ref.current.canvas unguarded — without a ref from the caller they throw on mount.
	const chartRef = useRef<any>(null);
	const pieHasNegativeValues = output.chartType === "pie" && hasNegativeValue(values);

	return (
		<OutputCard
			icon={faChartColumn}
			title={output.title}
			headerRight={<SaveOutputControl output={output} defaultName={defaultName} />}
		>
			<div className="h-96">
				{output.chartType === "bar" && (
					<BarChart
						ref={chartRef}
						labels={labelObjects}
						datasets={barValues}
						xLabelText={output.categoryLabel}
						yLabelText={valueLabel}
					/>
				)}
				{output.chartType === "line" && (
					<LineChart
						ref={chartRef}
						labels={labelObjects}
						datasets={values}
						xLabelText={output.categoryLabel}
						yLabelText={valueLabel}
						// A missing group is a real gap, not a continuous trend — unlike the v1
						// trendline, don't bridge over it (see shapeChartOutput's null comment).
						spanGaps={false}
						// Aggregates (average/sum/minimum) can land on 0 or go negative, unlike v1's
						// count-only assumption — no hard y-axis floor here.
						yAxisMin={null}
					/>
				)}
				{output.chartType === "pie" && pieHasNegativeValues && (
					<BarChart
						ref={chartRef}
						labels={labelObjects}
						datasets={barValues}
						xLabelText={output.categoryLabel}
						yLabelText={valueLabel}
					/>
				)}
				{output.chartType === "pie" && !pieHasNegativeValues && (
					<PieChart labels={output.labels} data={values} />
				)}
			</div>
			{pieHasNegativeValues && (
				<p className="mt-2 text-xs text-neutral-400">
					Negative values can&apos;t be shown as a pie chart — showing a bar chart instead.
				</p>
			)}
			{output.truncated && (
				<p className="mt-2 text-xs text-neutral-400">
					Showing the first 100 groups — narrow the query to see the rest.
				</p>
			)}
		</OutputCard>
	);
};

export default InlineChartBubble;
